import type { LLMRequest } from "./types";
import { getTotalCostFromApi } from "./modelPricing";

export interface CalculatedStats {
  promptTokens: number;
  responseTokens: number;
  totalTokens: number;
  processingTime: number;
  cost: number;
  tokensPerSecond: number;
}

const EMPTY_STATS: CalculatedStats = {
  promptTokens: 0,
  responseTokens: 0,
  totalTokens: 0,
  processingTime: 0,
  cost: 0,
  tokensPerSecond: 0,
};

/** Pretty-print a value that may already be a JSON string, falling back to its raw form. */
export function formatJSON(data: unknown): string {
  try {
    if (typeof data === "string") {
      return JSON.stringify(JSON.parse(data), null, 2);
    }
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

/**
 * Prompts are stored either as plain text or as a serialised chat-message array.
 * In the latter case, render each message as `[ROLE]` followed by its content.
 */
export function parsePromptContent(prompt: string): string {
  try {
    const parsed: unknown = JSON.parse(prompt);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return (parsed as { role?: string; content?: string }[])
        .map((msg) => {
          const role = msg.role ? `[${msg.role.toUpperCase()}]` : "[UNKNOWN]";
          return `${role}\n${msg.content || ""}`;
        })
        .join("\n\n---\n\n");
    }
    return prompt;
  } catch {
    return prompt;
  }
}

/** Token/time/cost roll-up for a request. Cost comes from the API, not recomputed here. */
export function calculateStats(llmRequest: LLMRequest | null): CalculatedStats {
  if (!llmRequest) {
    return EMPTY_STATS;
  }

  const promptTokens = llmRequest.prompt_tokens || 0;
  const responseTokens = llmRequest.completion_tokens || 0;
  const totalTokens = promptTokens + responseTokens;
  const processingTime = llmRequest.total_time_ms || 0;
  const cost =
    getTotalCostFromApi(llmRequest.input_cost_usd, llmRequest.output_cost_usd) || 0;

  return {
    promptTokens,
    responseTokens,
    totalTokens,
    processingTime,
    cost,
    tokensPerSecond:
      processingTime > 0 ? Math.round((totalTokens / processingTime) * 1000) : 0,
  };
}

export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.warn("Clipboard write failed:", err);
  }
}
